import { ProgressBar } from "@faire/slate/components/progress-bar";
import {
  PlaygroundLocalMsg,
  usePlaygroundLocalMsg,
} from "@faire/prototype-playground/platform/localization";

import { cn } from "../../lib/utils";
import { usePlaygroundImageWithFallback } from "../../hooks/usePlaygroundImageWithFallback";

import type { BrandSideCartItem } from "./BrandSideCart";

export interface GlobalSideCartBrand {
  brandName: string;
  items: BrandSideCartItem[];
}

export interface GlobalSideCartBrandListProps {
  brands: GlobalSideCartBrand[];
  onSelectBrand: (brandName: string) => void;
  onClose?: () => void;
  className?: string;
  /** Order minimum in dollars applied to every brand's cart. Defaults to 150. */
  minimum?: number;
  /** Extra className applied to every item-preview `<img>` (e.g. a tint overlay). */
  imageOverlayClassName?: string;
}

const MAX_PREVIEW_IMAGES = 4;

/**
 * Multi-brand sidecart landing view: one row per brand with a small item
 * preview, mirroring the brand-cart-list left rail on the cart details page
 * (BrandCartTile / BrandCartTileImageList). Clicking a row opens that
 * brand's individual cart.
 */
export default function GlobalSideCartBrandList({
  brands,
  onSelectBrand,
  onClose,
  className,
  minimum = 150,
  imageOverlayClassName,
}: GlobalSideCartBrandListProps) {
  const closeLabel = usePlaygroundLocalMsg("Close cart");

  return (
    <div
      className={cn(
        "bg-fs-surface-primary border-fs-border-muted flex h-full w-[320px] shrink-0 flex-col border-l xl:w-[360px]",
        className
      )}
    >
      <div className="flex items-center justify-between p-6 pb-4">
        <h4 className="type-fs-subheading-medium text-fs-text-primary">
          <PlaygroundLocalMsg defaultMessage="Carts" />
        </h4>
        {onClose ? (
          <button
            type="button"
            aria-label={closeLabel}
            onClick={onClose}
            className="text-fs-text-primary flex h-6 w-6 shrink-0 items-center justify-center"
          >
            <CloseIcon />
          </button>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-6 pb-6">
        {brands.length === 0 ? (
          <p className="type-fs-paragraph text-fs-text-subdued py-4">
            <PlaygroundLocalMsg defaultMessage="Your cart is empty." />
          </p>
        ) : (
          brands.map((brand) => (
            <BrandRow
              key={brand.brandName}
              brand={brand}
              minimum={minimum}
              onClick={() => onSelectBrand(brand.brandName)}
              imageOverlayClassName={imageOverlayClassName}
            />
          ))
        )}
      </div>
    </div>
  );
}

function BrandRow({
  brand,
  minimum,
  onClick,
  imageOverlayClassName,
}: {
  brand: GlobalSideCartBrand;
  minimum: number;
  onClick: () => void;
  imageOverlayClassName?: string;
}) {
  const itemCount = brand.items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = brand.items.reduce(
    (sum, item) => sum + item.unitPrice * item.quantity,
    0
  );
  const progress = minimum > 0 ? Math.min(1, subtotal / minimum) : 1;
  const preview = brand.items.slice(0, MAX_PREVIEW_IMAGES);
  const extraCount = Math.max(0, brand.items.length - MAX_PREVIEW_IMAGES);
  const itemCountLabel = usePlaygroundLocalMsg(
    itemCount === 1 ? "{count} item" : "{count} items",
    { count: itemCount }
  );

  return (
    <button
      type="button"
      onClick={onClick}
      className="border-fs-border-muted hover:border-fs-action-border-default flex flex-col gap-3 rounded border p-3 text-left transition-colors"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="type-fs-paragraph-medium text-fs-text-primary truncate">
          {brand.brandName}
        </p>
        <p className="type-fs-paragraph text-fs-text-subdued shrink-0">
          {itemCountLabel}
        </p>
      </div>

      <ProgressBar progress={progress} variant="success" showIcon />

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          {preview.map((item, index) => (
            <PreviewThumbnail
              key={`${item.token}-${index}`}
              item={item}
              imageOverlayClassName={imageOverlayClassName}
            />
          ))}
          {extraCount > 0 ? (
            <div className="bg-fs-surface-secondary type-fs-label text-fs-text-subdued flex size-10 shrink-0 items-center justify-center rounded">
              +{extraCount}
            </div>
          ) : null}
        </div>
        <p className="type-fs-paragraph-medium text-fs-text-primary shrink-0">
          {formatMoney(subtotal)}
        </p>
      </div>
    </button>
  );
}

function PreviewThumbnail({
  item,
  imageOverlayClassName,
}: {
  item: BrandSideCartItem;
  imageOverlayClassName?: string;
}) {
  const { src, onError } = usePlaygroundImageWithFallback(item.imageUrl);
  return (
    <picture className="block size-10 shrink-0 overflow-hidden rounded">
      <img
        src={src}
        onError={onError}
        alt={item.name}
        referrerPolicy="no-referrer"
        className={cn("h-full w-full object-cover", imageOverlayClassName)}
      />
    </picture>
  );
}

function formatMoney(value: number): string {
  const fixed = value.toFixed(2);
  return fixed.endsWith(".00") ? `$${Math.round(value)}` : `$${fixed}`;
}

function CloseIcon() {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}
